import { FC, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { Box, Button, Icon, Stack, Table } from '@chakra-ui/react';

import { useCreateApplicationDoc } from '~/core/shared/api/middle-office/application/hooks';
import { IconClose } from '~/core/shared/assets';
import {
  BulkDropzone,
  Dialog,
  FormField,
  Input,
  Select,
  Typeahead,
} from '~/core/shared/components';
import { DocStatusEnum, EMPTY_ARR } from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { FileRequestObject } from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { DocsTabContext } from '../application/tabs';

type AdditionalDocumentationFormValuesDynamic = {
  [key: string]: string | undefined;
};

interface DocumentPayload {
  names: string[];
  owners: string[];
  statuses: string[];
  types: string[];
  documentTypeCategory: string[];
}

type DocCreateModalProps = {
  documentTypeCategory: string[];
};

const docStatusOptions = Object.values(DocStatusEnum).map((type) => ({
  label: type,
  value: type,
}));

export const DocCreateModal: FC<DocCreateModalProps> = ({
  documentTypeCategory,
}) => {
  const [validationObject, setValidationObject] = useState<{
    [key: string]: yup.StringSchema;
  }>({});
  const [files, setFiles] = useState<File[]>(EMPTY_ARR);
  const {
    uuid,
    definition,
    docCreateModalApi,
    docTypes,
    documentOwnerOptions,
  } = useContext(DocsTabContext);
  const { createDoc, isPending } = useCreateApplicationDoc(definition ?? '');

  const resolver = useFormResolver(yup.object(validationObject));
  const form = useForm<AdditionalDocumentationFormValuesDynamic>({
    resolver,
  });

  // TODO: @haxxxton refactor this to use an array for known validation schemas
  useEffect(() => {
    const newValidationObject: { [key: string]: yup.StringSchema } = {};

    files.forEach((file, i) => {
      newValidationObject[`documentOwner${i}`] = yup.string();
      newValidationObject[`documentName${i}`] = yup.string();
      newValidationObject[`documentType${i}`] = yup.string().trim().required();
      newValidationObject[`documentStatus${i}`] = yup
        .string()
        .trim()
        .required();
    });

    setValidationObject(newValidationObject);
  }, [files]);

  const onSubmit = async (
    formValues: AdditionalDocumentationFormValuesDynamic,
  ): Promise<void> => {
    if (files.length === 0) {
      handleError('Please choose a file');
      return;
    }

    const payload: DocumentPayload = {
      documentTypeCategory,
      names: [],
      owners: [],
      statuses: [],
      types: [],
    };

    const fileObjects: FileRequestObject[] = files.map((file, i) => ({
      file,
      key: `${i}`,
    }));

    Object.keys(formValues).forEach((key) => {
      const value = formValues[key];
      const match = key.match(/(\D+)(\d+)/);

      if (match) {
        const [, prop, indexStr] = match;
        const index = Number(indexStr);
        const fileElementExists = fileObjects.some(
          (item) => item.key === indexStr,
        );

        if (fileElementExists && value) {
          switch (prop) {
            case 'documentName':
              payload.names[index] = value;
              break;
            case 'documentOwner':
              payload.owners[index] = value;
              break;
            case 'documentStatus':
              payload.statuses[index] = value;
              break;
            case 'documentType':
              payload.types[index] = value;
              break;
            default:
          }
        }
      }
    });

    await createDoc({
      uuid,
      data: {
        files: fileObjects,
        json: payload,
      },
    });
    form.reset();
    setFiles(EMPTY_ARR);
    docCreateModalApi.onClose();
  };

  const removeBulkFile = (index: number) => {
    const reducedFiles = [...files];

    reducedFiles.splice(index, 1);

    reducedFiles.forEach((_, newIndex) => {
      form.setValue(
        `documentName${newIndex}`,
        form.getValues(`documentName${newIndex + (newIndex >= index ? 1 : 0)}`),
      );
      form.setValue(
        `documentType${newIndex}`,
        form.getValues(`documentType${newIndex + (newIndex >= index ? 1 : 0)}`),
      );
      form.setValue(
        `documentOwner${newIndex}`,
        form.getValues(
          `documentOwner${newIndex + (newIndex >= index ? 1 : 0)}`,
        ),
      );
      form.setValue(
        `documentStatus${newIndex}`,
        form.getValues(
          `documentStatus${newIndex + (newIndex >= index ? 1 : 0)}`,
        ),
      );
    });

    setFiles(reducedFiles);
  };

  return (
    <Dialog
      header="Upload a new document"
      onOpenChange={docCreateModalApi.onOpenChange}
      open={docCreateModalApi.open}
      size="2xl"
      subHeader="Upload attachment attached to the application"
    >
      <Box maxH="600px" overflowY="auto">
        <Stack gap={5}>
          <BulkDropzone
            label="New documents"
            name="newDocuments"
            setFiles={(value) => setFiles(value)}
          />

          {files.length > 0 && (
            <FormProvider {...form}>
              <Box as="form" onSubmit={form.handleSubmit(onSubmit)}>
                <Table.ScrollArea>
                  <Table.Root>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader key={uuidv4()}>
                          Filename
                        </Table.ColumnHeader>
                        <Table.ColumnHeader key={uuidv4()}>
                          Document type
                        </Table.ColumnHeader>
                        <Table.ColumnHeader key={uuidv4()}>
                          Status
                        </Table.ColumnHeader>
                        <Table.ColumnHeader key={uuidv4()}>
                          For
                        </Table.ColumnHeader>
                        <Table.ColumnHeader key={uuidv4()}>
                          Name
                        </Table.ColumnHeader>
                        <Table.ColumnHeader key={uuidv4()} />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {files.map((data, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Table.Row key={i}>
                          <>
                            <Table.Cell maxW="145px" whiteSpace="break-spaces">
                              {data.name}
                            </Table.Cell>
                            <Table.Cell>
                              <FormField
                                borderColor="gray.300"
                                component={Typeahead}
                                groups={docTypes}
                                label="Type"
                                name={`documentType${i}`}
                                required
                                size="lg"
                                variant="outline"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <FormField
                                borderColor="gray.300"
                                component={Select}
                                label="Status"
                                name={`documentStatus${i}`}
                                options={docStatusOptions}
                                required
                                size="lg"
                                textTransform="capitalize"
                                variant="outline"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <FormField
                                borderColor="gray.300"
                                component={Select}
                                groups={documentOwnerOptions}
                                isClearable
                                label="Document owner"
                                name={`documentOwner${i}`}
                                size="lg"
                                textTransform="capitalize"
                                variant="outline"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <FormField
                                borderColor="gray.300"
                                component={Input}
                                label="Name"
                                name={`documentName${i}`}
                                placeholder="Add optional name for the document"
                                size="lg"
                                variant="outline"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Icon
                                _hover={{ cursor: 'pointer' }}
                                as={IconClose}
                                boxSize={6}
                                onClick={(e) => removeBulkFile(i)}
                              />
                            </Table.Cell>
                          </>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>

                <Stack direction="row" justifyContent="end" mt={5}>
                  <Button
                    colorPalette="gray"
                    fontSize="md"
                    fontWeight="normal"
                    onClick={docCreateModalApi.onClose}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    colorPalette="primary"
                    fontSize="md"
                    fontWeight="bold"
                    loading={isPending}
                    type="submit"
                    variant="solid"
                  >
                    Save
                  </Button>
                </Stack>
              </Box>
            </FormProvider>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};
